import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiClient from '@/lib/apiClient';
import type { RootState } from '@/store';

export interface ContentOpportunity {
  id?: string;
  domain_id: string;
  target_url: string;
  target_audit_checks?: Record<string, unknown>;
  selected_prompts?: string[];
  selected_sources?: string[];
  generated_topic?: string;
  aeo_details?: {
    word_count?: number;
    metadata?: { meta_title?: string; meta_description?: string; slug?: string };
    headings?: Record<string, number>;
    paragraphs?: number;
    content_structure?: { average_words_per_paragraph?: number; heading_to_paragraph_ratio?: string };
  };
  article_type?: string;
  mention_competitors?: boolean;
  language?: string;
  generated_draft_title?: string;
  generated_draft_body?: string; // JSON string to parse on UI
  // Phase 1 Data
  outline?: Record<string, unknown>;
  competitor_facts?: Record<string, unknown>[];
  updated_at?: string; // ISO timestamp
  status?: string; // e.g., 'pending_review', 'published', 'archived'
  is_placeholder?: boolean;
  creation_started_at?: string;
  creation_progress?: number; // 0-100 (estimation during polling)
}

export interface ContentsState {
  statusByDomain: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>;
  errorByDomain: Record<string, string | undefined>;
  contentsByDomain: Record<string, ContentOpportunity[]>;
  // Track which content ids are currently being deleted per domain (to disable buttons / show spinners)
  deletingIdsByDomain: Record<string, string[]>;
  // Track which content ids are currently having their status updated
  updatingStatusIdsByDomain: Record<string, string[]>;
}

const initialState: ContentsState = {
  statusByDomain: {},
  errorByDomain: {},
  contentsByDomain: {},
  deletingIdsByDomain: {},
  updatingStatusIdsByDomain: {},
};

// Fetch content opportunities for a domain with caching guard
export const fetchDomainContents = createAsyncThunk<{
  domainId: string; data: ContentOpportunity[];
}, string, { state: RootState }>(
  'contents/fetchDomainContents',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/contents`);
      const payload: unknown = res.data;
      const dataProp = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty('data'))
        ? (payload as { data: unknown }).data
        : payload;
      const arr: ContentOpportunity[] = Array.isArray(dataProp) ? (dataProp as ContentOpportunity[]) : [];
      // Assume backend always returns a stable `id` for each content; do not synthesize fallback ids anymore.
      // Filter out any items without id to avoid inconsistent client behavior.
      const normalized = arr.filter(it => !!it && typeof it === 'object' && (it as any).id) as ContentOpportunity[];
      return { domainId, data: normalized };
    } catch (err: unknown) {
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to load content opportunities');
    }
  },
  {
    condition: (domainId: string, { getState }) => {
      const state = getState() as RootState;
      const st = state.contents.statusByDomain[domainId];
      // Cache any succeeded response (including empty arrays) to avoid infinite refetch loops.
      // Use fetchDomainContentsForce when you explicitly want to refresh.
      if (st === 'loading' || st === 'succeeded') return false;
      return true;
    }
  }
);

export const deleteDomainContent = createAsyncThunk<{
  domainId: string; contentId: string;
}, { domainId: string; contentId: string }, { state: RootState }>(
  'contents/deleteDomainContent',
  async ({ domainId, contentId }, { rejectWithValue }) => {
    try {
      // Try to delete on server; if endpoint not available, server may return 404/405.
      await apiClient.delete(`/v1/domains/${domainId}/contents/${contentId}`);
      return { domainId, contentId };
    } catch (err: unknown) {
      // If delete fails (unknown endpoint), surface a friendly message
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to delete content');
    }
  }
);

// Update a content's status (pending <-> posted)
export const updateDomainContentStatus = createAsyncThunk<{
  domainId: string; contentId: string; status: string; updated?: ContentOpportunity;
}, { domainId: string; contentId: string; status: string; previousStatus?: string }>(
  'contents/updateDomainContentStatus',
  async ({ domainId, contentId, status }, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiClient.patch(`/v1/domains/${domainId}/contents/${contentId}/status`, { status });
      const payload: unknown = res.data;
      const dataProp = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty('data'))
        ? (payload as { data: unknown }).data
        : payload;
      const updated = (dataProp && typeof dataProp === 'object') ? (dataProp as ContentOpportunity) : undefined;
      if (!updated || !(updated as any).id) {
        // Fallback: refresh the list to pick up server-updated fields like updated_at
        await dispatch(fetchDomainContentsForce(domainId) as any);
        return { domainId, contentId, status };
      }
      return { domainId, contentId, status: updated.status || status, updated };
    } catch (err: unknown) {
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to update status');
    }
  }
);

// General update for content fields (e.g. body, title)
export const updateDomainContent = createAsyncThunk<{
  domainId: string; contentId: string; updated: ContentOpportunity;
}, { domainId: string; contentId: string; data: Partial<ContentOpportunity> }>(
  'contents/updateDomainContent',
  async ({ domainId, contentId, data }, { rejectWithValue }) => {
    try {
      const res = await apiClient.patch(`/v1/domains/${domainId}/contents/${contentId}`, data);
      const payload: unknown = res.data;
      const dataProp = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty('data'))
        ? (payload as { data: unknown }).data
        : payload;
      const updated = (dataProp && typeof dataProp === 'object') ? (dataProp as ContentOpportunity) : undefined;
      return { domainId, contentId, updated: updated || { ...data, id: contentId, domain_id: domainId } as ContentOpportunity };
    } catch (err: unknown) {
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to save changes');
    }
  }
);

// Trigger full content generation from Phase 1 details (outline)
export const generateDomainContent = createAsyncThunk<{
  domainId: string; contentId: string; updated: ContentOpportunity; jobId?: string;
}, { domainId: string; contentId: string }>(
  'contents/generateDomainContent',
  async ({ domainId, contentId }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/v1/domains/${domainId}/contents/${contentId}/generate`);
      const payload: unknown = res.data;
      const dataProp = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty('data'))
        ? (payload as { data: unknown }).data
        : payload;
      const jobId = (dataProp && typeof dataProp === 'object' && 'job_id' in (dataProp as Record<string, unknown>))
        ? (dataProp as { job_id: string }).job_id
        : undefined;
      const updated = (dataProp && typeof dataProp === 'object') ? (dataProp as ContentOpportunity) : undefined;
      return { domainId, contentId, updated: updated || { id: contentId, domain_id: domainId } as ContentOpportunity, jobId };
    } catch (err: unknown) {
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to generate content');
    }
  }
);

// Force fetch without cache guard, useful after creating content to refresh from backend
export const fetchDomainContentsForce = createAsyncThunk<{
  domainId: string; data: ContentOpportunity[];
}, string>(
  'contents/fetchDomainContentsForce',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/contents`);
      const payload: unknown = res.data;
      const dataProp = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty('data'))
        ? (payload as { data: unknown }).data
        : payload;
      const arr: ContentOpportunity[] = Array.isArray(dataProp) ? (dataProp as ContentOpportunity[]) : [];
      const normalized = arr.filter(it => !!it && typeof it === 'object' && (it as any).id) as ContentOpportunity[];
      return { domainId, data: normalized };
    } catch (err: unknown) {
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to load content opportunities');
    }
  }
);

const contentsSlice = createSlice({
  name: 'contents',
  initialState,
  reducers: {
    setContentStatus: (state, action: { payload: { domainId: string; contentId: string; status: string } }) => {
      const { domainId, contentId, status } = action.payload;
      const arr = state.contentsByDomain[domainId] || [];
      const idx = arr.findIndex((x) => (x.id || '') === contentId);
      if (idx >= 0) {
        arr[idx] = { ...arr[idx], status } as ContentOpportunity;
        state.contentsByDomain[domainId] = arr.slice();
      }
    },
    addContentDraft: (state, action: { payload: { domainId: string; draft: Partial<ContentOpportunity> } }) => {
      const { domainId, draft } = action.payload;
      const arr = state.contentsByDomain[domainId] || [];
      // Prefer backend-provided id; fallback only for purely client-side drafts (temporary)
      const id = draft.id || `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const nowIso = new Date().toISOString();
      const newItem: ContentOpportunity = {
        id,
        domain_id: domainId,
        target_url: draft.target_url || 'https://example.com/new-draft',
        target_audit_checks: draft.target_audit_checks || {},
        selected_prompts: draft.selected_prompts || draft.selected_prompts || [],
        selected_sources: draft.selected_sources || [],
        generated_topic: draft.generated_topic,
        aeo_details: draft.aeo_details,
        article_type: draft.article_type,
        mention_competitors: draft.mention_competitors,
        generated_draft_title: draft.generated_draft_title,
        generated_draft_body: draft.generated_draft_body,
        updated_at: nowIso,
        status: draft.status || 'pending',
      };
      state.contentsByDomain[domainId] = [newItem, ...arr];
    },
    addContentPlaceholder: (state, action: { payload: { domainId: string; title?: string; placeholderId?: string; startedAt?: string; articleType?: string } }) => {
      const { domainId, title, placeholderId, startedAt, articleType } = action.payload;
      const arr = state.contentsByDomain[domainId] || [];
      const nowIso = new Date().toISOString();
      const placeholder: ContentOpportunity = {
        id: placeholderId || `placeholder_${Date.now()}`,
        domain_id: domainId,
        target_url: 'pending://content',
        generated_draft_title: 'Creating content...',
        updated_at: startedAt || nowIso,
        status: 'creating',
        is_placeholder: true,
        creation_started_at: startedAt || nowIso,
        creation_progress: 0,
        selected_prompts: [],
        selected_sources: [],
        article_type: articleType,
      };
      state.contentsByDomain[domainId] = [placeholder, ...arr];
    },
    updatePlaceholderProgress: (state, action: { payload: { domainId: string; placeholderId: string; progress: number } }) => {
      const { domainId, placeholderId, progress } = action.payload;
      const arr = state.contentsByDomain[domainId] || [];
      const idx = arr.findIndex(x => x.id === placeholderId);
      if (idx >= 0 && arr[idx].is_placeholder) {
        arr[idx] = { ...arr[idx], creation_progress: Math.min(100, Math.max(0, progress)) };
        state.contentsByDomain[domainId] = arr.slice();
      }
    },
    removePlaceholder: (state, action: { payload: { domainId: string; placeholderId: string } }) => {
      const { domainId, placeholderId } = action.payload;
      const arr = state.contentsByDomain[domainId] || [];
      state.contentsByDomain[domainId] = arr.filter(x => x.id !== placeholderId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDomainContents.pending, (state, action) => {
        const id = action.meta.arg;
        state.statusByDomain[id] = 'loading';
        state.errorByDomain[id] = undefined;
      })
      .addCase(fetchDomainContents.fulfilled, (state, action) => {
        const { domainId, data } = action.payload;
        state.statusByDomain[domainId] = 'succeeded';
        state.errorByDomain[domainId] = undefined;
        const existing = state.contentsByDomain[domainId] || [];
        const placeholders = existing.filter(c => c.is_placeholder);
        // For each placeholder, check if a real item (non-placeholder) created after its start exists
        const merged: ContentOpportunity[] = [];
        for (const ph of placeholders) {
          const satisfied = (data || []).some(d => !d.is_placeholder && d.updated_at && ph.creation_started_at && d.updated_at >= ph.creation_started_at);
          if (!satisfied) merged.push(ph); // keep placeholder if not yet satisfied
        }
        state.contentsByDomain[domainId] = [...merged, ...(data || [])];
      })
      .addCase(fetchDomainContents.rejected, (state, action) => {
        const id = action.meta.arg;
        state.statusByDomain[id] = 'failed';
        state.errorByDomain[id] = typeof action.payload === 'string' ? action.payload : 'Unknown error';
      })
      .addCase(fetchDomainContentsForce.pending, (state, action) => {
        const id = action.meta.arg;
        state.statusByDomain[id] = 'loading';
        state.errorByDomain[id] = undefined;
      })
      .addCase(fetchDomainContentsForce.fulfilled, (state, action) => {
        const { domainId, data } = action.payload;
        state.statusByDomain[domainId] = 'succeeded';
        state.errorByDomain[domainId] = undefined;
        const existing = state.contentsByDomain[domainId] || [];
        const placeholders = existing.filter(c => c.is_placeholder);
        const merged: ContentOpportunity[] = [];
        for (const ph of placeholders) {
          const satisfied = (data || []).some(d => !d.is_placeholder && d.updated_at && ph.creation_started_at && d.updated_at >= ph.creation_started_at);
          if (!satisfied) merged.push(ph);
        }
        state.contentsByDomain[domainId] = [...merged, ...(data || [])];
      })
      .addCase(fetchDomainContentsForce.rejected, (state, action) => {
        const id = action.meta.arg;
        state.statusByDomain[id] = 'failed';
        state.errorByDomain[id] = typeof action.payload === 'string' ? action.payload : 'Unknown error';
      })
      .addCase(deleteDomainContent.pending, (state, action) => {
        const { domainId, contentId } = action.meta.arg;
        const arr = state.deletingIdsByDomain[domainId] || [];
        if (!arr.includes(contentId)) state.deletingIdsByDomain[domainId] = [...arr, contentId];
      })
      .addCase(deleteDomainContent.fulfilled, (state, action) => {
        const { domainId, contentId } = action.payload;
        const arr = state.contentsByDomain[domainId] || [];
        state.contentsByDomain[domainId] = arr.filter((x) => (x.id || '') !== contentId);
        // remove from deleting list
        const delArr = state.deletingIdsByDomain[domainId] || [];
        state.deletingIdsByDomain[domainId] = delArr.filter(x => x !== contentId);
      });
    builder.addCase(deleteDomainContent.rejected, (state, action) => {
      const { domainId, contentId } = action.meta.arg;
      // remove id from deleting list
      const arr = state.deletingIdsByDomain[domainId] || [];
      state.deletingIdsByDomain[domainId] = arr.filter(x => x !== contentId);
      // optionally set domain error (would not override existing fetch error if present)
      if (typeof action.payload === 'string') {
        state.errorByDomain[domainId] = action.payload;
      }
    });
    // Status update lifecycle
    builder.addCase(updateDomainContentStatus.pending, (state, action) => {
      const { domainId, contentId, status } = action.meta.arg;
      const list = state.updatingStatusIdsByDomain[domainId] || [];
      if (!list.includes(contentId)) state.updatingStatusIdsByDomain[domainId] = [...list, contentId];
      // Optimistic update
      const arr = state.contentsByDomain[domainId] || [];
      const idx = arr.findIndex(x => (x.id || '') === contentId);
      if (idx >= 0) {
        arr[idx] = { ...arr[idx], status } as ContentOpportunity;
        state.contentsByDomain[domainId] = arr.slice();
      }
    });
    builder.addCase(updateDomainContentStatus.fulfilled, (state, action) => {
      const { domainId, contentId, status, updated } = action.payload;
      // Remove from updating list
      const list = state.updatingStatusIdsByDomain[domainId] || [];
      state.updatingStatusIdsByDomain[domainId] = list.filter(x => x !== contentId);
      const arr = state.contentsByDomain[domainId] || [];
      const idx = arr.findIndex(x => (x.id || '') === contentId);
      if (idx >= 0) {
        if (updated) {
          arr[idx] = { ...arr[idx], ...updated } as ContentOpportunity;
        } else {
          arr[idx] = { ...arr[idx], status } as ContentOpportunity;
        }
        state.contentsByDomain[domainId] = arr.slice();
      }
    });
    builder.addCase(updateDomainContentStatus.rejected, (state, action) => {
      const { domainId, contentId, previousStatus } = action.meta.arg;
      // Remove from updating list
      const list = state.updatingStatusIdsByDomain[domainId] || [];
      state.updatingStatusIdsByDomain[domainId] = list.filter(x => x !== contentId);
      // Revert optimistic change if previousStatus provided
      if (previousStatus) {
        const arr = state.contentsByDomain[domainId] || [];
        const idx = arr.findIndex(x => (x.id || '') === contentId);
        if (idx >= 0) {
          arr[idx] = { ...arr[idx], status: previousStatus } as ContentOpportunity;
          state.contentsByDomain[domainId] = arr.slice();
        }
      }
      if (typeof action.payload === 'string') {
        state.errorByDomain[domainId] = action.payload;
      }
    });

    // Content update (general)
    builder.addCase(updateDomainContent.fulfilled, (state, action) => {
      const { domainId, contentId, updated } = action.payload;
      const arr = state.contentsByDomain[domainId] || [];
      const idx = arr.findIndex(x => (x.id || '') === contentId);
      if (idx >= 0) {
        arr[idx] = { ...arr[idx], ...updated } as ContentOpportunity; 
        state.contentsByDomain[domainId] = arr.slice();
      }
    });

    // Generate content
    builder.addCase(generateDomainContent.pending, (state, action) => {
        const { domainId, contentId } = action.meta.arg;
        const current = state.updatingStatusIdsByDomain[domainId] || [];
        if (!current.includes(contentId)) {
          state.updatingStatusIdsByDomain[domainId] = [...current, contentId];
        }
    });
    builder.addCase(generateDomainContent.fulfilled, (state, action) => {
        const { domainId, contentId, updated } = action.payload;
        // Update content — ensure status is at least 'running' even if API didn't return full doc
        const list = state.contentsByDomain[domainId] || [];
        const idx = list.findIndex(c => c.id === contentId);
        if (idx >= 0) {
          const merged = { ...list[idx], ...updated };
          if (!merged.status || merged.status === 'outline') {
            merged.status = 'running';
          }
          list[idx] = merged;
          state.contentsByDomain[domainId] = [...list];
        }
        // Remove from updating
        const current = state.updatingStatusIdsByDomain[domainId] || [];
        state.updatingStatusIdsByDomain[domainId] = current.filter(id => id !== contentId);
    });
    builder.addCase(generateDomainContent.rejected, (state, action) => {
        const { domainId, contentId } = action.meta.arg;
        const current = state.updatingStatusIdsByDomain[domainId] || [];
        state.updatingStatusIdsByDomain[domainId] = current.filter(id => id !== contentId);
    });
  }
});

export default contentsSlice.reducer;
export type { ContentOpportunity as TContentOpportunity };
export const { setContentStatus, addContentDraft, addContentPlaceholder, updatePlaceholderProgress, removePlaceholder } = contentsSlice.actions;
